import { Button } from '@/components/ui/button'
import { Trans, useLingui } from '@lingui/react/macro'
import useAtomicBatch from '@/hooks/use-atomic-batch'
import useIsComplianceRestricted from '@/hooks/use-is-compliance-restricted'
import { cn } from '@/lib/utils'
import { chainIdAtom } from '@/state/atoms'
import { indexDTFAtom } from '@/state/dtf/atoms'
import { getFolioRoute } from '@/utils'
import { ROUTES } from '@/utils/constants'
import { useAccountModal, useConnectModal } from '@rainbow-me/rainbowkit'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  ArrowLeft,
  ArrowUpRight,
  Info,
  Loader2,
  MoveRight,
  OctagonAlert,
  Combine,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount, useDisconnect } from 'wagmi'
import { wizardStepAtom } from '../atoms'
import { panelModeAtom } from '../../atoms'
import ComplianceAlert from '../../../components/compliance-alert'
import { useTrackAsyncZap } from '../hooks/use-track-async-zap'
import SwitchToManualLink from '../components/switch-to-manual-link'

const LEARN_MORE_URL = 'https://docs.safe.global/home/what-is-safe'

const ProcessStep = ({ label }: { label: string }) => (
  <div className="flex shrink-0 items-center">
    <span className="whitespace-nowrap text-sm font-medium text-foreground">
      {label}
    </span>
  </div>
)

const ProcessArrow = () => (
  <div className="flex shrink-0 items-center justify-center">
    <MoveRight size={16} className="text-muted-foreground" />
  </div>
)

const GnosisRequired = () => {
  const { openConnectModal } = useConnectModal()
  const { openAccountModal } = useAccountModal()
  const { isConnected } = useAccount()
  const { disconnectAsync } = useDisconnect()
  const { atomicSupported, isLoading } = useAtomicBatch()
  const setStep = useSetAtom(wizardStepAtom)
  const setPanelMode = useSetAtom(panelModeAtom)
  const indexDTF = useAtomValue(indexDTFAtom)
  const chainId = useAtomValue(chainIdAtom)
  // The swap lives on the issuance page (panelMode 'swap'). Use an absolute
  // route so this works whether the wizard is embedded there or standalone at
  // /issuance/automated — a relative ".." resolves to the DTF overview when
  // embedded.
  const swapRoute = indexDTF
    ? getFolioRoute(indexDTF.id, chainId, ROUTES.ISSUANCE)
    : ''
  const [showRequirements, setShowRequirements] = useState(false)
  const [requirementsCardHeight, setRequirementsCardHeight] = useState<number>()
  const cardStackRef = useRef<HTMLDivElement>(null)
  const isRestricted = useIsComplianceRestricted()
  const { track } = useTrackAsyncZap()
  const { t } = useLingui()

  // The requirements screen prompts to connect (or switch wallet). Once a
  // connection lands and the capability probe confirms support, advance on its
  // own — otherwise the screen would keep showing the connect/incompatible
  // card after a successful connection until a manual refresh.
  useEffect(() => {
    if (showRequirements && isConnected && !isLoading && atomicSupported) {
      setStep('configure')
    }
  }, [showRequirements, isConnected, isLoading, atomicSupported, setStep])

  // Tear down any lingering connector state before opening the modal. Without
  // this, a fresh Safe-over-WalletConnect connect can hang in "connecting" and
  // never resolve until a manual refresh (same fix as the navbar connect).
  const handleConnect = async () => {
    try {
      await disconnectAsync()
    } catch {
      // ignore — nothing to disconnect
    }
    openConnectModal?.()
  }
  const title = showRequirements
    ? t`Smart Account Required`
    : t`Automated Mint / Redeem`
  const body = showRequirements
    ? t`Automated minting and redemption require a wallet with smart account support. Hardware Wallets are not supported.`
    : t`Mint large USDC amounts through batched CoW Swap orders, or redeem DTFs into the underlying assets. Recommended for market makers or transactions over 50,000 USDC.`
  const verifiedWallets = (
    <div>
      <p className="text-md text-primary dark:text-foreground font-medium mb-4">
        <Trans>Known supported wallets</Trans>
      </p>
      <div className="flex items-center gap-2">
        <a
          href="https://metamask.io/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t`Open MetaMask website`}
          className="rounded-[8px]"
        >
          <img
            src="/svgs/Metamask.svg"
            alt=""
            className="size-8 rounded-[8px] border border-input shadow-md"
          />
        </a>
        <a
          href="https://www.ambire.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t`Open Ambire website`}
          className="rounded-[8px]"
        >
          <img
            src="/svgs/Ambire.svg"
            alt=""
            className="size-8 rounded-[8px] border border-input shadow-md"
          />
        </a>
        <a
          href="https://safe.global/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t`Open Safe website`}
          className="rounded-[8px]"
        >
          <img
            src="/svgs/Safe.svg"
            alt=""
            className="size-8 rounded-[8px] border border-input shadow-md"
          />
        </a>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        <Trans>
          <span className="font-medium text-foreground">Note:</span> Some
          wallets, like MetaMask, need smart accounts enabled.
        </Trans>
      </p>
    </div>
  )
  const swapGuidance = (
    <div className="flex flex-col p-2 bg-card/50">
      <div className="flex items-center rounded-full p-2 bg-card justify-between border border-amber-700/20 dark:border-amber-300/25">
        <div className="flex w-fit items-center h-8 gap-2 text-amber-700 dark:text-amber-300">
          <div className="flex items-center rounded-full justify-center h-8 w-8 bg-amber-700/15 border border-amber-700/20 dark:bg-amber-300/10 dark:border-amber-300/25">
            <Info size={20} strokeWidth={1.5} />
          </div>
          <p className="text-md font-medium">
            <Trans>Most users should use Swap</Trans>
          </p>
        </div>
        <Button
          asChild
          size="sm"
          className="h-8 w-fit shrink-0 rounded-full px-3 !transition-none"
        >
          <Link to={swapRoute} onClick={() => setPanelMode('swap')}>
            <Trans>Use Swap</Trans>
          </Link>
        </Button>
      </div>
      <div className="flex flex-col px-4 pb-3 pt-4 gap-2">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-medium">
            <Trans>Before using automated minting</Trans>
          </p>
          <p className="text-sm text-muted-foreground">
            <Trans>
              Automated minting is an advanced feature. For most people, simple
              swaps are recommended.
            </Trans>
          </p>
        </div>
      </div>
    </div>
  )

  const processIllustration = (
    <div className="px-5 pb-3 mt-1">
      <div className="flex gap-2">
        <ProcessStep label={t`You fund`} />
        <ProcessArrow />
        <ProcessStep label={t`CoW routes`} />
        <ProcessArrow />
        <ProcessStep label={t`Assets arrive`} />
        <ProcessArrow />
        <ProcessStep label={t`You mint`} />
      </div>
    </div>
  )

  const handleShowRequirements = () => {
    track('gnosis_continue')
    if (isConnected && atomicSupported) {
      setStep('configure')
      return
    }

    setRequirementsCardHeight(
      cardStackRef.current?.getBoundingClientRect().height
    )
    setShowRequirements(true)
  }

  return (
    <div className="w-full">
      <div className="flex flex-col justify-center min-h-[calc(100vh-136px)] w-full lg:min-h-[calc(100vh-100px)]">
        <ComplianceAlert className="sm:w-full max-w-[468px] mb-2" />
        <div
          ref={cardStackRef}
          className={cn(
            'w-full max-w-[468px] mx-auto flex flex-col rounded-4xl border-2 border-card overflow-hidden',
            isRestricted && 'pointer-events-none select-none opacity-50'
          )}
          aria-disabled={isRestricted || undefined}
        >
          {!showRequirements && swapGuidance}
          <div
            className={cn(
              'p-1 overflow-hidden w-full flex flex-col transition-[min-height] duration-700 ease-out',
              showRequirements ? 'bg-background/70' : 'bg-card'
            )}
            style={
              showRequirements && requirementsCardHeight
                ? { minHeight: requirementsCardHeight }
                : undefined
            }
          >
            <div className="flex flex-1 flex-col">
              {/* Title + description */}
              <div
                className={cn(
                  'mt-5 flex flex-col gap-1',
                  showRequirements && 'flex-1'
                )}
              >
                {showRequirements ? (
                  <div className="mb-auto flex items-center justify-between gap-3 px-5 pb-10">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="size-8 rounded-full !transition-none"
                      aria-label={t`Back to automated minting introduction`}
                      onClick={() => setShowRequirements(false)}
                    >
                      <ArrowLeft size={16} />
                    </Button>
                    {isConnected && (
                      <div
                        className={cn(
                          'h-8 px-3 rounded-full flex items-center gap-1 text-sm font-light',
                          'border border-amber-500/25 bg-amber-500/10 text-amber-700 dark:border-amber-300/25 dark:bg-amber-300/10 dark:text-amber-300'
                        )}
                      >
                        {isLoading ? (
                          <>
                            <Loader2
                              size={16}
                              strokeWidth={1.5}
                              className="animate-spin"
                            />
                            <span>
                              <Trans>Checking wallet...</Trans>
                            </span>
                          </>
                        ) : (
                          <>
                            <OctagonAlert size={16} strokeWidth={1.5} />
                            <span>
                              <Trans>Incompatible wallet</Trans>
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-5 mb-[64px]">
                      <Combine size={16} strokeWidth={1.5} />
                      <p className="text-md">
                        <Trans>Advanced</Trans>
                      </p>
                    </div>
                    <h2 className="text-xl font-semibold px-5 mb-1 text-foreground">
                      {title}
                    </h2>
                    <p className="text-muted-foreground font-light px-5 mb-1 max-w-[435px]">
                      {body}
                    </p>
                    {processIllustration}
                  </>
                )}
                {showRequirements && (
                  <>
                    <h2 className="text-xl font-semibold px-5 mb-1 mt-8 text-amber-700 dark:text-amber-300">
                      {title}
                    </h2>
                    <p className="text-muted-foreground font-light px-5 mb-2 max-w-[435px]">
                      {body}
                    </p>
                  </>
                )}
                {showRequirements ? (
                  <div className="text-base bg-card rounded-3xl overflow-hidden mt-1">
                    {isConnected ? (
                      <div>
                        <div className="p-5">{verifiedWallets}</div>
                        <div className="flex flex-col gap-2 sm:flex-row p-2 pt-0">
                          <Button
                            size="lg"
                            className="h-[49px] rounded-xl sm:flex-[2] !transition-none"
                            onClick={openAccountModal}
                          >
                            <Trans>Switch wallet</Trans>
                          </Button>
                          <Button
                            asChild
                            variant="outline"
                            size="lg"
                            className="h-[49px] rounded-xl px-1 text-primary hover:text-primary sm:flex-1 !transition-none"
                          >
                            <a
                              href={LEARN_MORE_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Trans>Learn More</Trans>
                              <ArrowUpRight size={16} className="ml-1" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="p-5">{verifiedWallets}</div>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row p-2 pt-0">
                          <Button
                            size="lg"
                            className="h-[49px] rounded-xl sm:flex-[2] !transition-none"
                            onClick={handleConnect}
                          >
                            <Trans>Connect Wallet</Trans>
                          </Button>
                          <Button
                            asChild
                            variant="outline"
                            size="lg"
                            className="h-[49px] rounded-xl px-1 text-primary hover:text-primary sm:flex-1 !transition-none"
                          >
                            <a
                              href={LEARN_MORE_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Trans>Learn More</Trans>
                              <ArrowUpRight size={16} className="ml-1" />
                            </a>
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="p-1">
                    <Button
                      size="lg"
                      className="h-[49px] w-full rounded-2xl bg-foreground dark:bg-foreground/10 dark:text-foreground !transition-none"
                      onClick={handleShowRequirements}
                      disabled={isConnected && isLoading}
                    >
                      {isConnected && isLoading ? (
                        <Trans>Checking wallet...</Trans>
                      ) : (
                        <Trans>Continue</Trans>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <SwitchToManualLink />
      </div>
    </div>
  )
}

export default GnosisRequired
